"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.seoArticleWriter = exports.seoArticleWriterResultSchema = exports.jsonLdBlogPostingSchema = exports.seoArticleWriterArgsSchema = exports.targetPlatformSchema = exports.outputFormatSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
// Output format types
exports.outputFormatSchema = zod_1.z.enum(['html', 'markdown', 'plaintext', 'docx', 'auto']);
exports.targetPlatformSchema = zod_1.z.enum(['wordpress', 'shopify', 'framer', 'webflow', 'other']);
// SEO Article Writer Arguments Schema
exports.seoArticleWriterArgsSchema = zod_1.z.object({
    keywords: zod_1.z
        .union([
        zod_1.z
            .string()
            .min(1, 'At least one keyword is required')
            .describe('Comma-separated keywords: "keyword1, keyword2, keyword3"'),
        zod_1.z
            .array(zod_1.z.string())
            .min(1, 'At least one keyword is required')
            .describe('Array of keywords: ["keyword1", "keyword2", "keyword3"]'),
    ])
        .refine(function (val) {
        if (typeof val === 'string') {
            // Split by comma, trim, and filter out empty strings
            return (val
                .split(',')
                .map(function (s) { return s.trim(); })
                .filter(Boolean).length > 0);
        }
        if (Array.isArray(val)) {
            return val.filter(Boolean).length > 0;
        }
        return false;
    }, { message: 'At least one keyword is required' })
        .describe('required: Target keywords for SEO optimization. Accepts either a comma-separated string or an array of strings'),
    content: zod_1.z
        .string()
        .min(1, 'Content is required')
        .describe('required: Main topic or brief description for the article (e.g., "AI automation tools for businesses")'),
    knowledgebase: zod_1.z
        .string()
        .optional()
        .describe('optional: Additional knowledge base or context to inform the article writing'),
    referenceContent: zod_1.z
        .string()
        .optional()
        .describe('optional: URL to extract and repurpose content (e.g., https://example.com/blog/post) or raw text/HTML content to expand on'),
    writingGuideline: zod_1.z
        .string()
        .optional()
        .describe('optional: Writing style guidelines - tone, voice, target audience, or specific instructions for the content writer'),
    targetDomain: zod_1.z
        .string()
        .optional()
        .describe('optional: Target domain or website URL for the article (e.g., "example.com" or "https://example.com")'),
    targetPlatform: exports.targetPlatformSchema
        .optional()
        .describe('optional: Target publishing platform for format optimization (wordpress, shopify, framer, webflow, other)'),
    outputFormat: exports.outputFormatSchema
        .optional()
        .describe('optional: Preferred output format (html, markdown, plaintext, docx, auto). Auto selects optimal format based on targetPlatform'),
    internalLinks: zod_1.z
        .array(zod_1.z.object({
        url: zod_1.z.string().url('Must be a valid URL'),
        anchorText: zod_1.z
            .string()
            .min(1, 'Anchor text is required')
            .optional()
            .describe('Optional anchor text for the link. If not provided, will be auto-generated'),
        context: zod_1.z
            .string()
            .optional()
            .describe('Optional context about where/why this link should be included'),
    }))
        .optional()
        .describe('optional: Manual internal links to include in the article. The system will automatically find additional internal links from your site.'),
    enableAutoInternalLinking: zod_1.z
        .boolean()
        .optional()
        .default(true)
        .describe('optional: Whether to automatically discover and add internal links from your site (default: true)'),
    maxInternalLinks: zod_1.z
        .number()
        .min(3)
        .max(15)
        .optional()
        .default(15)
        .describe('optional: Maximum number of internal links to include (3-15, default: 15)'),
    internalLinkingGuidance: zod_1.z
        .string()
        .optional()
        .describe('optional: Custom instructions for internal link selection and placement (e.g., "Focus on product pages", "Prioritize new collections", "Link to educational content")'),
    includeCTAButtons: zod_1.z
        .boolean()
        .optional()
        .default(false)
        .describe('optional: Whether to include CTA buttons distributed throughout the article (default: false)'),
    ctaButtonStyle: zod_1.z
        .enum(['primary', 'secondary', 'outline'])
        .optional()
        .default('primary')
        .describe('optional: Style of CTA buttons - primary (blue), secondary (gray), or outline (transparent with border)'),
    ctaButtonText: zod_1.z
        .array(zod_1.z.string().min(1, 'CTA text cannot be empty'))
        .max(3)
        .optional()
        .describe('optional: Custom CTA button texts (max 3). If not provided, will auto-generate based on internal links'),
    ctaButtonUrls: zod_1.z
        .array(zod_1.z.string().url('Must be a valid URL'))
        .max(3)
        .optional()
        .describe('optional: Custom URLs for CTA buttons (max 3). If not provided, will use internal links or placeholder URLs'),
    enableImageGeneration: zod_1.z
        .boolean()
        .optional()
        .default(false)
        .describe('optional: Whether to generate AI images for the article (default: false)'),
    imagePrompt: zod_1.z
        .string()
        .optional()
        .describe('optional: Custom prompt template for image generation (e.g., "professional business photo", "modern minimalist style")'),
    imageStyle: zod_1.z
        .string()
        .optional()
        .describe('optional: Style guidance for generated images (e.g., "photorealistic", "illustration", "digital art", "corporate")'),
});
// Content section schemas
var headerSectionSchema = zod_1.z.object({
    type: zod_1.z.literal('header'),
    level: zod_1.z.number().min(1).max(6),
    content: zod_1.z.string(),
    id: zod_1.z.string().optional(),
    keywords: zod_1.z.array(zod_1.z.string()).optional(),
});
var paragraphSectionSchema = zod_1.z.object({
    type: zod_1.z.literal('paragraph'),
    content: zod_1.z.string(),
    id: zod_1.z.string().optional(),
});
var imageSectionSchema = zod_1.z.object({
    type: zod_1.z.literal('image'),
    src: zod_1.z.string().url(),
    alt: zod_1.z.string().max(125),
    title: zod_1.z.string().optional(),
    caption: zod_1.z.string().optional(),
    width: zod_1.z.number().min(1).optional(),
    height: zod_1.z.number().min(1).optional(),
    formats: zod_1.z
        .object({
        webp: zod_1.z.string().url().optional(),
        avif: zod_1.z.string().url().optional(),
    })
        .optional(),
    isGenerated: zod_1.z.boolean().optional(),
    generatedPrompt: zod_1.z.string().optional(),
    generatedStyle: zod_1.z.string().optional(),
});
var listSectionSchema = zod_1.z.object({
    type: zod_1.z.literal('list'),
    style: zod_1.z.enum(['ordered', 'unordered']),
    items: zod_1.z.array(zod_1.z.string()),
});
var tableSectionSchema = zod_1.z.object({
    type: zod_1.z.literal('table'),
    caption: zod_1.z.string().optional(),
    headers: zod_1.z.array(zod_1.z.string()),
    rows: zod_1.z.array(zod_1.z.array(zod_1.z.string())),
});
var faqSectionSchema = zod_1.z.object({
    type: zod_1.z.literal('faq'),
    items: zod_1.z.array(zod_1.z.object({
        question: zod_1.z.string(),
        answer: zod_1.z.string(),
        id: zod_1.z.string().optional(),
    })),
});
var codeSectionSchema = zod_1.z.object({
    type: zod_1.z.literal('code'),
    language: zod_1.z.string().optional(),
    content: zod_1.z.string(),
    caption: zod_1.z.string().optional(),
});
var blockquoteSectionSchema = zod_1.z.object({
    type: zod_1.z.literal('blockquote'),
    content: zod_1.z.string(),
    citation: zod_1.z.string().optional(),
    url: zod_1.z.string().url().optional(),
});
var contentSectionSchema = zod_1.z.union([
    headerSectionSchema,
    paragraphSectionSchema,
    imageSectionSchema,
    listSectionSchema,
    tableSectionSchema,
    faqSectionSchema,
    codeSectionSchema,
    blockquoteSectionSchema,
]);
// JSON-LD Schema.org structured data schemas
var jsonLdImageSchema = zod_1.z.object({
    '@type': zod_1.z.literal('ImageObject'),
    url: zod_1.z.string().url(),
    width: zod_1.z.number().optional(),
    height: zod_1.z.number().optional(),
});
var jsonLdPersonSchema = zod_1.z.object({
    '@type': zod_1.z.literal('Person'),
    name: zod_1.z.string(),
    url: zod_1.z.string().url().optional(),
});
var jsonLdOrganizationSchema = zod_1.z.object({
    '@type': zod_1.z.literal('Organization'),
    name: zod_1.z.string(),
    logo: zod_1.z.union([zod_1.z.string().url(), jsonLdImageSchema]).optional(),
});
var jsonLdWebPageSchema = zod_1.z.object({
    '@type': zod_1.z.literal('WebPage'),
    '@id': zod_1.z.string().url(),
});
exports.jsonLdBlogPostingSchema = zod_1.z.object({
    '@context': zod_1.z.literal('https://schema.org'),
    '@type': zod_1.z.union([zod_1.z.literal('Article'), zod_1.z.literal('BlogPosting')]),
    mainEntityOfPage: jsonLdWebPageSchema,
    headline: zod_1.z.string(),
    description: zod_1.z.string(),
    image: zod_1.z.union([zod_1.z.string().url(), jsonLdImageSchema]).optional(),
    author: jsonLdPersonSchema,
    publisher: jsonLdOrganizationSchema,
    datePublished: zod_1.z.string().datetime(),
    dateModified: zod_1.z.string().datetime(),
    keywords: zod_1.z.string(),
});
// SEO Article Writer Result Schema (Simplified for CMS)
exports.seoArticleWriterResultSchema = zod_1.z.object({
    metadata: zod_1.z.object({
        title: zod_1.z.string().max(200).min(1),
        slug: zod_1.z
            .string()
            .regex(/^[a-z0-9-]+$/)
            .max(100),
        publishedDate: zod_1.z.string().datetime(),
        targetDomain: zod_1.z.string().optional(),
        targetPlatform: exports.targetPlatformSchema.optional(),
        outputFormat: exports.outputFormatSchema.optional(),
    }),
    seo: zod_1.z.object({
        metaTitle: zod_1.z.string().min(1), // Removed max(60) - treat as guideline, not strict requirement
        metaDescription: zod_1.z.string().min(1), // Removed max(160) - treat as guideline, not strict requirement
        targetKeywords: zod_1.z.array(zod_1.z.string()),
    }),
    content: zod_1.z.object({
        format: zod_1.z.enum(['structured', 'markdown', 'html', 'plaintext']).default('structured'),
        wordCount: zod_1.z.number().min(0).optional(),
        readingTime: zod_1.z.number().optional(),
        sections: zod_1.z.array(contentSectionSchema).min(1),
    }),
    // Multiple format outputs for maximum platform compatibility
    outputs: zod_1.z
        .object({
        html: zod_1.z
            .string()
            .optional()
            .nullable()
            .describe('Clean HTML format for WordPress, Framer, Webflow'),
        markdown: zod_1.z
            .string()
            .optional()
            .nullable()
            .describe('Markdown format for traditional publishing'),
        plaintext: zod_1.z
            .string()
            .optional()
            .nullable()
            .describe('Plain text format with basic formatting'),
        docxUrl: zod_1.z
            .string()
            .url()
            .optional()
            .nullable()
            .describe('DOCX file URL for document sharing'),
    })
        .optional(),
    // Internal links information
    internalLinks: zod_1.z
        .object({
        linksAdded: zod_1.z.array(zod_1.z.object({
            url: zod_1.z.string().url(),
            anchorText: zod_1.z.string(),
            context: zod_1.z.string().optional(),
            priority: zod_1.z.enum(['high', 'medium', 'low']).optional().nullable(),
            targetSection: zod_1.z.string().optional().nullable(),
            isManual: zod_1.z
                .boolean()
                .optional()
                .nullable()
                .describe('Whether this was a manually specified link'),
        })),
        totalUrlsAnalyzed: zod_1.z.number().optional(),
        sitemapMethod: zod_1.z.enum(['sitemap', 'internal_crawl', 'both']).optional(),
        shopifyUrlsIncluded: zod_1.z.number().optional(),
        reasoning: zod_1.z.string().optional(),
    })
        .optional()
        .describe('Internal links automatically added to the article'),
    // Backward compatibility fields
    docxUrl: zod_1.z.string().url().optional(),
    articleMarkdown: zod_1.z.string().optional(),
    // File output for DOCX when generated
    file: zod_1.z
        .object({
        type: zod_1.z.literal('file'),
        fileType: zod_1.z.union([zod_1.z.literal('pdf'), zod_1.z.literal('docx')]),
        filename: zod_1.z.string(),
        source: zod_1.z.object({
            kind: zod_1.z.union([zod_1.z.literal('raw'), zod_1.z.literal('url')]),
            value: zod_1.z.string(),
        }),
    })
        .optional(),
    // JSON-LD structured data for SEO
    jsonLd: exports.jsonLdBlogPostingSchema
        .optional()
        .describe('Schema.org JSON-LD structured data for enhanced SEO'),
    // Generated images information
    generatedImages: zod_1.z
        .array(zod_1.z.object({
        url: zod_1.z.string().url(),
        alt: zod_1.z.string(),
        prompt: zod_1.z.string(),
        style: zod_1.z.string().optional(),
        placement: zod_1.z.string().describe('Where the image was placed in the article'),
        sectionIndex: zod_1.z.number().optional(),
        filename: zod_1.z.string(),
    }))
        .optional()
        .describe('AI-generated images included in the article'),
});
// Tool definition
exports.seoArticleWriter = (0, common_1.createToolMetadata)({
    id: 'seoArticleWriter',
    displayName: 'SEO Article Writer',
    displayDescription: 'Create SEO-optimized articles from topics or existing content. Supports URL content extraction for repurposing, keyword optimization, and multiple output formats.',
    category: 'ai',
    color: common_1.BRAND_COLORS.AI,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.AI),
    icon: 'text-edit',
    argsSchema: exports.seoArticleWriterArgsSchema,
}, exports.seoArticleWriterResultSchema);
